import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { addUpdateUser, getUserByUserId } from "../../store/common/authSlice";
import { useToast } from "../../components/utilities/Toast";

import FormField from "../../components/profiles/FormField";
import LabPreferenceSection from "../../components/profiles/LabPreferenceSection";
import ConsentSection from "../../components/profiles/ConsentSection";
import ConfirmModal from "../../components/utilities/confirmModal";
import { Helmet } from "react-helmet-async";
import IntroModal from "../../components/utilities/IntroModal";
import { isUrisBrand } from "../../utils/brandLogo";

const Profile = () => {
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const location = useLocation();
  const [showIntroModal, setShowIntroModal] = useState(false);

  const isSmile = location.pathname.includes("/smiledesign");

  const {
    user,
    userById,
    isLoadingAddUpdateUser,
    isLoading,
    isLoadingUserById,
  } = useAppSelector((state) => state.auth);

  const isUris = isUrisBrand(user) && !isSmile;
  const variant: "cases" | "smile" | "uris" = isSmile
    ? "smile"
    : isUris
      ? "uris"
      : "cases";

  const isUnverified = !!user?.token && !user?.verified && !user?.verifySmile;

  const defaultLabPreferences = {
    parameters: {
      occlusal_clearance: "",
      proximal_contact: "",
      cement_gap: "",
      extra_cement_gap: "",
      drill_radius: "",
      margin_parameter: "",
      pontic_design: "",
    },
    anatomy: { library: "", anatomy_preference: "" },
    library_preference: { any_preference: "", best_match: "" },
    software: { scanner_used: "", design_software: "", software_version: "" },
    modelPreference: { type: "" },
    files_preference: { type: "" },
    otherPreference: {
      natural_anatomy: "",
      ovate_pontic: "",
      read_notes_carefully: "",
    },
  };


  const [form, setForm] = useState<any>({
    organisation: "",
    organisation_type: "",
    address: "",
    post_code: "",
    phone_number: "",
    region: "",
    user_acquisition_channel: "",
    lab_preferences: {
      parameters: {
        occlusal_clearance: "",
        proximal_contact: "",
        cement_gap: "",
        extra_cement_gap: "",
        drill_radius: "",
        margin_parameter: "",
        pontic_design: "",
      },
      anatomy: { library: "", anatomy_preference: "" },
      library_preference: { any_preference: "", best_match: "" },
      software: { scanner_used: "", design_software: "", software_version: "" },
      modelPreference: { type: "" },
      files_preference: { files: "" },
      otherPreference: { natural_anatomy: "", ovate_pontic: "", read_notes_carefully: "" },
    },
  });

  const [consent, setConsent] = useState({ user: false, patient: false });
  const [isEditMode, setIsEditMode] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  useEffect(() => {
    if (!userById && user?.user_id)
      dispatch(getUserByUserId(user.user_id.toString()));
  }, [user?.user_id]);

  useEffect(() => {
    if (userById) {
      const parsedLabPrefs =
        typeof userById.lab_preferences === "string"
          ? JSON.parse(userById.lab_preferences)
          : userById.lab_preferences;

      setForm((prev: any) => ({
        ...prev,
        ...userById,
        lab_preferences: {
          ...defaultLabPreferences,
          ...parsedLabPrefs,
          parameters: {
            ...defaultLabPreferences.parameters,
            ...parsedLabPrefs?.parameters,
          },
          anatomy: {
            ...defaultLabPreferences.anatomy,
            ...parsedLabPrefs?.anatomy,
          },
          library_preference: {
            ...defaultLabPreferences.library_preference,
            ...parsedLabPrefs?.library_preference,
          },
          software: {
            ...defaultLabPreferences.software,
            ...parsedLabPrefs?.software,
          },
          modelPreference: {
            ...defaultLabPreferences.modelPreference,
            ...parsedLabPrefs?.modelPreference,
          },
          files_preference: {
            ...defaultLabPreferences.files_preference,
            ...parsedLabPrefs?.files_preference,
          },
          otherPreference: {
            ...defaultLabPreferences.otherPreference,
            ...parsedLabPrefs?.otherPreference,
          },
        },
      }));

      setConsent({
        user: userById.user_consent,
        patient: userById.patient_consent,
      });

      if (
        userById.organisation ||
        userById.organisation_type ||
        userById.address
      ) {
        setIsEditMode(true);
      }
    }
  }, [userById]);


  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setForm((prev: any) => ({ ...prev, [name]: value }));
  };

  const handleLabPrefChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    section: string,
  ) => {
    const { name, value } = e.target;
    setForm((prev: any) => ({
      ...prev,
      lab_preferences: {
        ...prev.lab_preferences,
        [section]: { ...prev.lab_preferences[section], [name]: value },
      },
    }));
  };

  const handleConsentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setConsent((prev) => ({ ...prev, [name]: checked }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowConfirmModal(true);
  };

  const submitProfile = async () => {
    const finalData = {
      ...form,
      lab_preferences: JSON.stringify(form.lab_preferences),
      ...(isEditMode ? {} : {
        user_consent: consent.user ? 1 : 0,
        patient_consent: consent.patient ? 1 : 0
      }),
    };

    try {
      const resultAction = await dispatch(
        addUpdateUser({ formData: finalData }),
      );
      if (addUpdateUser.fulfilled.match(resultAction)) {
        showToast(
          `Profile ${isEditMode ? "updated" : "created"} successfully!`,
          "success",
        );

        if (!isEditMode) {
          setShowIntroModal(true);
        }

        // REFRESH USER DATA
        if (user?.user_id) {
          dispatch(getUserByUserId(user.user_id.toString()));
        }
      } else throw new Error(resultAction.payload || "Profile update failed.");
    } catch (error: any) {
      showToast(error.message || "Something went wrong", "error");
    }
  };

  const isFormComplete =
    (form.organisation?.trim() || "") &&
    (form.organisation_type?.trim() || "") &&
    (form.address?.trim() || "") &&
    (form.post_code?.trim() || "") &&
    (form.phone_number?.trim() || "") &&
    (form.region?.trim() || "") &&
    (form.user_acquisition_channel?.trim() || "") &&
    (isEditMode || (consent.user && consent.patient));

  if (isLoading || isLoadingAddUpdateUser || isLoadingUserById) {
    return (
      <div
        className={`w-full h-screen flex p-12 justify-center text-xl ${
          isSmile ? "text-[#8b8f8d]" : "text-gray-400"
        }`}
      >
        Loading Your Profile...
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Profile</title>
      </Helmet>
      <div
        className={
          isSmile ? "-m-4 md:-m-6 p-4 md:p-6 bg-[#080808] min-h-screen" : ""
        }
      >
      <div
        className={`relative rounded-3xl w-full p-2 md:p-8 ${
          isSmile
            ? "bg-[#101211] border border-[#1f2221] text-white"
            : isUris
              ? "border border-[#6b2430] bg-[#141517] text-white"
              : "bg-white"
        }`}
      >
        <h1
          className={`text-xl md:text-[30px] font-bold mb-1 md:mb-2 ${
            isSmile
              ? "bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent"
              : isUris
                ? "text-white"
                : "text-gray-800"
          }`}
        >
          Welcome {userById?.user_name}!
        </h1>

        {!isEditMode && (
          <p
            className={`text-sm md:text-[15px] mt-2 md:mt-4 ${
              isSmile ? "text-[#8b8f8d]" : isUris ? "text-[#c5c7cc]" : "text-gray-800"
            }`}
          >
            Please complete your profile information to continue using
            ToothSketch services.
          </p>
        )}

        {user && user?.token && !user?.verified && !user?.verifySmile ? (
          <p
            className={`text-sm md:text-[16px] mt-2 md:mt-4 ${
              isSmile ? "text-green-400" : "text-green-600"
            }`}
          >
            Your registration is almost complete! ToothSketch officials will
            shortly complete profile verification, after which you can start
            using our services.
          </p>
        ) : null}

        <h2
          className={`text:md md:text-[15px] mt-4 font-bold uppercase mb-4 border-b-2 ${
            isSmile
              ? "text-white border-cyan-500"
              : isUris
                ? "text-white border-[#c31c30]"
                : "text-gray-800 border-[#2fa9bf]"
          }`}
        >
          Profile Information
        </h2>

        <form onSubmit={handleSubmit}>
          {[
            { label: "Organisation Name", name: "organisation" },
            { label: "Shipping Address", name: "address" },
            { label: "Post Code", name: "post_code" },
            { label: "Phone Number", name: "phone_number" },
          ].map((field) => (
            <FormField
              key={field.name}
              {...field}
              value={form[field.name]}
              onChange={handleChange}
              variant={variant}
            />
          ))}

          <FormField
            label="Region"
            name="region"
            value={form.region}
            onChange={handleChange}
            options={["US-east", "US-west", "UK", "EU", "IN", "Other"]}
            variant={variant}
          />

          <FormField
            label="Organisation Type"
            name="organisation_type"
            value={form.organisation_type}
            onChange={handleChange}
            options={["Dental Practice", "Lab"]}
            variant={variant}
          />

          <FormField
            label="How did you hear about us?"
            name="user_acquisition_channel"
            value={form.user_acquisition_channel}
            onChange={handleChange}
            options={[
              "A colleague",
              "Internet Search",
              "Existing User",
              "Other",
            ]}
            variant={variant}
          />

          {/* {userById?.partner && ( */}
          <LabPreferenceSection
            labPreferences={form.lab_preferences}
            handleChange={handleLabPrefChange}
            variant={variant}
          />
          {/* )} */}

          {!isEditMode && (
            <ConsentSection
              consent={consent}
              handleChange={handleConsentChange}
              variant={variant}
            />
          )}

          <div className="w-full flex justify-start mt-4">
            <button
              type="submit"
              disabled={!isFormComplete}
              className={`mt-2 px-6 py-2 font-semibold rounded-md ${isFormComplete
                ? isSmile
                  ? "bg-gradient-to-r from-cyan-400 to-blue-500 text-white hover:from-cyan-300 hover:to-blue-400"
                  : isUris
                    ? "bg-[#e11d2e] text-white hover:bg-[#c31c30]"
                    : "bg-[#2fa9bf] text-white hover:bg-[#279eb3]"
                : isSmile
                  ? "bg-[#1f2221] text-[#5c605e] cursor-not-allowed"
                  : isUris
                    ? "bg-[#2a2b31] text-[#6d6f76] cursor-not-allowed"
                    : "bg-blue-300 text-gray-100 cursor-not-allowed"
                }`}
            >
              {isEditMode ? "Update Profile" : "Create Profile"}
            </button>
          </div>
        </form>

        <ConfirmModal
          isOpen={showConfirmModal}
          onClose={() => setShowConfirmModal(false)}
          onConfirm={() => {
            setShowConfirmModal(false);
            submitProfile();
          }}
          title={`${isEditMode ? "update" : "create"} Profile`}
          message={`Are you sure you want to ${isEditMode ? "update" : "create"
            } your profile?`}
          confirmColor="blue"
          confirmLabel="Yes, Confirm"
          cancelLabel="Cancel"
        />
      </div>
      </div>
      {showIntroModal && (
        <IntroModal
          onClose={() => {
            setShowIntroModal(false);
            window.location.reload();
          }}
        />
      )}
    </>
  );
};

export default Profile;
